import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { BASE } from './_Layout'
import InfoTip from './InfoTip'
import {
    ClientStatus,
    EngagementLevel,
    MockFamily,
    MOCK_FAMILIES,
    MOCK_FORMER_PATIENTS,
    MOCK_MEMBERS,
} from './mockData'
import { useProviderSession } from './previewSession'

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_BADGE: Record<ClientStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
    onboarding: { label: 'Onboarding', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    at_risk: { label: 'At risk', cls: 'bg-red-50 text-red-700 border-red-200' },
}

const ENGAGEMENT_BADGE: Record<EngagementLevel, { label: string; dot: string; text: string }> = {
    high: { label: 'High', dot: 'bg-green-500', text: 'text-neutral-700' },
    medium: { label: 'Medium', dot: 'bg-amber-500', text: 'text-neutral-700' },
    low: { label: 'Low', dot: 'bg-red-500', text: 'text-neutral-700' },
}

export default function ProviderFamilies() {
    const session = useProviderSession()
    const isAdmin = session?.role === 'admin'
    const isCoordinator = session?.role === 'coordinator'
    const [families, setFamilies] = useState<MockFamily[]>(MOCK_FAMILIES)
    const former = MOCK_FORMER_PATIENTS
    const coordinators = MOCK_MEMBERS.filter((m) => m.role === 'coordinator').map((m) => ({
        userId: m.userId,
        name: m.name || m.email || 'Coordinator',
    }))
    const [search, setSearch] = useState('')

    function handleAssign(licenseId: string, coordinatorId: string) {
        const coordinator = coordinators.find((c) => c.userId === coordinatorId)
        setFamilies((prev) =>
            prev.map((c) =>
                c.licenseId === licenseId
                    ? {
                          ...c,
                          assignedTo: coordinatorId || null,
                          assignedToName: coordinator?.name ?? null,
                      }
                    : c
            )
        )
    }

    // Coordinators are scoped to their own caseload; admins and viewers see the
    // whole organization. The current coordinator is matched by signed-in email.
    const currentUserId = MOCK_MEMBERS.find((m) => m.email === session?.user.email)?.userId ?? null
    const scoped =
        isCoordinator && currentUserId ? families.filter((c) => c.assignedTo === currentUserId) : families

    const summary = {
        total: scoped.length,
        active: scoped.filter((c) => c.status === 'active').length,
        onboarding: scoped.filter((c) => c.status === 'onboarding').length,
        atRisk: scoped.filter((c) => c.status === 'at_risk').length,
    }
    const q = search.trim().toLowerCase()
    const filtered = q
        ? scoped.filter(
              (c) =>
                  (c.childName ?? '').toLowerCase().includes(q) ||
                  (c.parentName ?? '').toLowerCase().includes(q) ||
                  c.email.toLowerCase().includes(q)
          )
        : scoped

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Clients</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    {`${summary.total} clients ${isCoordinator ? 'on your caseload' : 'across your organization'}. ${summary.active} active, ${summary.onboarding} onboarding, ${summary.atRisk} at risk.`}
                </p>
            </div>

            <div className='relative mb-4'>
                <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
                <input
                    type='text'
                    placeholder='Search by child name, parent, or email'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className='w-full max-w-md pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm'
                />
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Client</th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Status
                                    <InfoTip text='Active: engaging regularly. Onboarding: still completing setup and early milestones. At risk: no meaningful app activity in 30+ days, stalled roadmap progress, or incomplete onboarding.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Engagement
                                    <InfoTip text='A High / Medium / Low rating from recent app activity (logins, roadmap steps, homework, saved resources, and chat) relative to the rest of the caseload.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>Last active</th>
                                {!isCoordinator && <th className='px-4 py-3 text-left font-medium'>Coordinator</th>}
                                <th className='px-4 py-3 text-right font-medium'></th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {filtered.map((c) => (
                                <tr key={c.licenseId} className='hover:bg-neutral-50/50'>
                                    <td className='px-4 py-3'>
                                        <div className='font-medium text-neutral-900'>
                                            {c.childName ?? c.email}
                                        </div>
                                        <div className='text-xs text-neutral-500'>
                                            {c.parentName ? `${c.parentName} · ` : ''}
                                            {c.email}
                                        </div>
                                    </td>
                                    <td className='px-4 py-3'>
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE[c.status].cls}`}
                                        >
                                            {STATUS_BADGE[c.status].label}
                                        </span>
                                    </td>
                                    <td className='px-4 py-3'>
                                        <span className='inline-flex items-center gap-1.5 text-xs'>
                                            <span className={`w-2 h-2 rounded-full ${ENGAGEMENT_BADGE[c.engagementLevel].dot}`} />
                                            <span className={ENGAGEMENT_BADGE[c.engagementLevel].text}>
                                                {ENGAGEMENT_BADGE[c.engagementLevel].label}
                                            </span>
                                        </span>
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>
                                        {formatDate(c.lastActiveAt) ?? 'Never'}
                                    </td>
                                    {!isCoordinator && (
                                        <td className='px-4 py-3'>
                                            {isAdmin ? (
                                                <select
                                                    value={c.assignedTo ?? ''}
                                                    onChange={(e) => handleAssign(c.licenseId, e.target.value)}
                                                    className='px-2 py-1.5 border border-neutral-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500'
                                                >
                                                    <option value=''>Unassigned</option>
                                                    {coordinators.map((coord) => (
                                                        <option key={coord.userId} value={coord.userId}>
                                                            {coord.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className='text-xs text-neutral-600'>
                                                    {c.assignedToName ?? 'Unassigned'}
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    <td className='px-4 py-3 text-right'>
                                        <Link
                                            to={`${BASE}/clients/${c.licenseId}`}
                                            className='text-sm text-brand-600 hover:underline'
                                        >
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filtered.length === 0 && (
                    <div className='py-12 text-center text-sm text-neutral-500'>
                        {families.length === 0
                            ? 'No registered clients yet. Clients appear here once an invited family creates their account.'
                            : 'No clients match this search.'}
                    </div>
                )}
            </div>

            {/* Former clients — sponsorship ended; org records preserved */}
            {isAdmin && former.length > 0 && (
                <div className='mt-8'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Former clients</h2>
                    <p className='text-sm text-neutral-600 mb-4'>
                        Sponsorships that have ended. Your team&apos;s notes and homework records are preserved;
                        the client&apos;s app activity is no longer visible.
                    </p>
                    <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                                    <tr>
                                        <th className='px-4 py-3 text-left font-medium'>Client</th>
                                        <th className='px-4 py-3 text-left font-medium'>Status</th>
                                        <th className='px-4 py-3 text-left font-medium'>Sponsored</th>
                                        <th className='px-4 py-3 text-right font-medium'>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-neutral-100'>
                                    {former.map((c) => (
                                        <tr key={c.licenseId} className='hover:bg-neutral-50/50'>
                                            <td className='px-4 py-3'>
                                                <div className='font-medium text-neutral-900'>
                                                    {c.childName ?? c.email}
                                                </div>
                                                <div className='text-xs text-neutral-500'>
                                                    {c.parentName ? `${c.parentName} · ` : ''}
                                                    {c.email}
                                                </div>
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className='inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-200 text-neutral-700 capitalize'>
                                                    {c.status}
                                                </span>
                                            </td>
                                            <td className='px-4 py-3 text-neutral-600'>
                                                {formatDate(c.activatedAt) ?? '—'} – {formatDate(c.endedAt) ?? '—'}
                                            </td>
                                            <td className='px-4 py-3 text-right'>
                                                <Link
                                                    to={`${BASE}/clients/former/${c.licenseId}`}
                                                    className='text-sm text-neutral-600 hover:underline'
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
